/**
 * Core CV data types
 */

export interface CV {
  id: string
  title: string
  templateId: string
  locale: string
  accentColor?: string
  createdAt: string
  updatedAt: string
  personalInfo: CVPersonalInfo
  experiences: CVExperience[]
  education: CVEducation[]
  skills: CVSkill[]
  languages: CVLanguage[]
  certifications: CVCertification[]
  customSections: CVCustomSection[]
}

export interface CVSummary {
  id: string
  title: string
  templateId: string
  updatedAt: string
}

export interface CVPersonalInfo {
  fullName: string
  jobTitle: string
  email: string
  phone: string
  address: string
  city: string
  country: string
  postalCode: string
  website: string
  linkedin: string
  summary: string
  photoUrl: string
  showPhoto?: boolean
}

export interface CVExperience {
  id: string
  company: string
  position: string
  location: string
  startDate: string
  endDate: string | null
  isCurrent: boolean
  description: string
  sortOrder: number
}

export interface CVEducation {
  id: string
  institution: string
  degree: string
  fieldOfStudy: string
  location: string
  startDate: string
  endDate: string | null
  isCurrent: boolean
  description: string
  grade: string
  sortOrder: number
}

export interface CVSkill {
  id: string
  name: string
  level: SkillLevel
  category: string
  sortOrder: number
}

export type SkillLevel = 'beginner' | 'intermediate' | 'advanced' | 'expert'

export interface CVLanguage {
  id: string
  name: string
  proficiency: LanguageProficiency
  sortOrder: number
}

export type LanguageProficiency = 'native' | 'fluent' | 'advanced' | 'intermediate' | 'basic'

export interface CVCertification {
  id: string
  name: string
  issuer: string
  issueDate: string
  expiryDate: string | null
  credentialId: string
  credentialUrl: string
  sortOrder: number
}

export interface CVCustomSection {
  id: string
  title: string
  content: string
  sortOrder: number
}

/**
 * API request/response types
 */

export interface CreateCVRequest {
  title?: string
  templateId?: string
  locale?: string
}

export interface UpdateCVRequest {
  title?: string
  templateId?: string
  locale?: string
  accentColor?: string
  personalInfo?: Partial<CVPersonalInfo>
  experiences?: CVExperience[]
  education?: CVEducation[]
  skills?: CVSkill[]
  languages?: CVLanguage[]
  certifications?: CVCertification[]
  customSections?: CVCustomSection[]
}

export interface PDFOptions {
  format?: 'A4' | 'Letter'
  margins?: {
    top: number
    right?: number
    bottom: number
    left?: number
    sides?: number
  }
}

/**
 * Factory function to create empty CV
 */
export function createEmptyCV(id: string): CV {
  const now = new Date().toISOString()
  return {
    id,
    title: 'Unavngivet CV',
    templateId: 'classic',
    locale: 'da-DK',
    createdAt: now,
    updatedAt: now,
    personalInfo: {
      fullName: '',
      jobTitle: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      country: '',
      postalCode: '',
      website: '',
      linkedin: '',
      summary: '',
      photoUrl: '',
      showPhoto: false,
    },
    experiences: [],
    education: [],
    skills: [],
    languages: [],
    certifications: [],
    customSections: [],
  }
}
